import { useState, useEffect } from "react";
import { ArrowRight, Check, Search } from "lucide-react";
import axios from "axios";
import { API } from "@/context/AuthContext";

const categoryLabels = { "coconut-fiber": "Coconut Fiber", "cocopeat-blocks": "Cocopeat Blocks", "coir-pith": "Coir Pith", "other-coir": "Other Coir" };

export default function ProductCatalog() {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => { axios.get(`${API}/products`).then(r => setProducts(r.data)).catch(() => {}); }, []);

  const filtered = products.filter(p => {
    const matchCat = cat === "all" || p.category === cat;
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) || p.description.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const categories = ["all", ...new Set(products.map(p => p.category))];

  return (
    <div className="section-padding bg-[#F9FAFB]" data-testid="product-catalog">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-serif text-navy mb-4 tracking-tight">Our <span className="text-forest">Products</span></h1>
          <p className="text-slate-500 text-lg font-sans max-w-2xl mx-auto">Explore our complete range of premium coconut fiber products.</p>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map(c => (
              <button key={c} onClick={() => setCat(c)} className={`px-4 py-2 text-sm font-medium font-sans transition-all ${cat === c ? "bg-navy text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-navy"}`} data-testid={`catalog-filter-${c}`}>
                {c === "all" ? "All" : categoryLabels[c] || c}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..." className="form-input pl-9 text-sm" data-testid="product-search" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(product => (
            <div key={product.id} className="bg-white shadow-lg shadow-slate-100/50 overflow-hidden group hover:shadow-xl transition-all" data-testid={`catalog-product-${product.id}`}>
              <div className="relative h-52 bg-gradient-to-br from-forest/15 to-coir/15 flex items-center justify-center overflow-hidden">
                {product.images?.[0] ? <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : (
                  <div className="text-center"><div className="w-16 h-16 bg-forest/20 rounded-full flex items-center justify-center mx-auto mb-2"><span className="text-forest text-2xl font-serif">{product.name[0]}</span></div></div>
                )}
                <div className="absolute top-3 right-3">
                  <span className={`px-2 py-1 text-xs font-medium font-sans ${product.availability === "in-stock" ? "bg-green-100 text-green-800" : product.availability === "limited" ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"}`}>{(product.availability || "").replace("-", " ")}</span>
                </div>
              </div>
              <div className="p-6">
                <p className="text-xs text-forest font-medium uppercase tracking-widest mb-2 font-sans">{(product.category || "").replace(/-/g, " ")}</p>
                <h3 className="text-xl font-serif text-navy mb-3 tracking-tight">{product.name}</h3>
                <p className="text-slate-500 text-sm mb-4 font-sans">{product.description}</p>
                {product.specifications && (
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {Object.entries(product.specifications).slice(0, 4).map(([k, v]) => (
                      <div key={k} className="flex items-center text-sm font-sans"><Check className="w-3.5 h-3.5 text-forest mr-1.5 flex-shrink-0" /><span className="text-slate-600 truncate">{k.replace(/_/g, " ")}: {v}</span></div>
                    ))}
                  </div>
                )}
                {product.price && (
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div>
                      <p className="text-xs text-slate-400 font-sans">Price (Domestic)</p>
                      <p className="text-lg font-bold text-navy font-mono">Rs.{product.price.domestic}<span className="text-sm font-normal text-slate-400">/{product.price.unit}</span></p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400 font-sans">MOQ</p>
                      <p className="text-sm font-medium text-slate-700 font-sans">{product.moq}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {filtered.length === 0 && <p className="text-center text-slate-400 py-12 font-sans">No products found.</p>}
      </div>
    </div>
  );
}
